import { useEffect, useState, type JSX } from "react";
import { useI18n } from "../../i18n";
import { usePwaRegistration } from "../../hooks/usePwaRegistration";
import { isWorkoutActive, onWorkoutActiveChange } from "../../hooks/workoutActive";

export function PwaUpdateToast(): JSX.Element | null {
  const { t } = useI18n();
  const { needRefresh, updateServiceWorker } = usePwaRegistration();
  const [workoutActive, setWorkoutActive] = useState(isWorkoutActive);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setWorkoutActive(isWorkoutActive());
    return onWorkoutActiveChange(() => setWorkoutActive(isWorkoutActive()));
  }, []);

  if (!needRefresh) return null;
  if (workoutActive) return null;
  if (dismissed) return null;

  return (
    <div
      role="status"
      data-testid="pwa-update-toast"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-xl items-center justify-between gap-4 border border-[var(--color-line-strong)] bg-[var(--color-slab)] p-4 shadow-lg"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <div className="flex flex-col gap-0.5">
        <span className="font-sans text-sm font-medium text-[var(--color-ink-hi)]">
          {t("pwa.update.available")}
        </span>
        <span className="font-sans text-xs text-[var(--color-ink-mute)]">
          {t("pwa.update.reassurance")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="pwa-update-dismiss"
          className="border border-[var(--color-line-strong)] px-3 py-2 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
          style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
        >
          {t("common.later")}
        </button>
        <button
          type="button"
          data-testid="pwa-update-reload"
          onClick={updateServiceWorker}
          className="bg-[var(--color-lime)] px-4 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-void)] hover:brightness-110 active:scale-95 transition-all"
          style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
        >
          {t("pwa.update.reload")}
        </button>
      </div>
    </div>
  );
}
